<template>
  <div class="conv-advanced-filter">
    <woot-modal-header-v2
      v-if="!skipHeader"
      :header-title="filterModalHeaderTitle"
      :header-content="filterModalSubTitle"
    >
      <template #titleIcon>
        <span class="material-symbols-outlined text-icon-primary">
          filter_alt
        </span>
      </template>
    </woot-modal-header-v2>

    <div
      ref="filtersBody"
      class="filters-body px-6 py-6 min-h-[260px] max-h-[600px]"
      :class="{ 'overflow-y-auto': isOverflowing }"
    >
      <div v-if="isFolderView">
        <label
          class="input-label text-[#1F2937] mb-1.5 leading-5 font-[400]"
          :class="{ error: !activeFolderNewName }"
        >
          {{ $t('FILTER.FOLDER_LABEL') }}
        </label>
        <input v-model="activeFolderNewName" type="text" class="folder-input" />
        <span v-if="!activeFolderNewName" class="message">
          {{ $t('FILTER.EMPTY_VALUE_ERROR') }}
        </span>
        <label class="mb-1.5 text-[#1F2937] leading-5 font-[400]">
          {{ $t('FILTER.FOLDER_QUERY_LABEL') }}
        </label>
      </div>
      <filter-input-box
        v-for="(filter, i) in appliedFilters"
        :key="i"
        v-model="appliedFilters[i]"
        :filter-groups="filterGroups"
        :input-type="
          getInputType(
            appliedFilters[i].attribute_key,
            appliedFilters[i].filter_operator
          )
        "
        :operators="getOperators(appliedFilters[i].attribute_key)"
        :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
        :show-query-operator="i !== appliedFilters.length - 1"
        :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
        :grouped-filters="true"
        :show-input-label="true"
        :v="$v.appliedFilters.$each[i]"
        :disable-remove-filter="appliedFilters.length === 1"
        @resetFilter="resetFilter(i, appliedFilters[i])"
        @removeFilter="removeFilter(i)"
      />
      <div class="mt-4">
        <button
          class="button-outline px-5 py-[11px] flex items-center justify-center gap-1"
          @click="appendNewFilter"
        >
          <span class="material-symbols-outlined">add</span>
          <span class="text-base">{{ $t('FILTER.ADD_CONDITION') }}</span>
        </button>
      </div>
    </div>

    <div
      v-if="!skipButtons"
      class="w-full px-6 py-4 z-[60] border-t border-gray-100"
      style="box-shadow: 0px -1px 4px #cacaca29"
    >
      <div class="flex flex-row justify-end gap-3 px-0 w-full">
        <woot-button
          class="cancel-filter-btn button clear text-base px-5 py-3"
          color-scheme="secondary"
          @click.prevent="onClose"
        >
          {{ $t('FILTER.CANCEL_BUTTON_LABEL') }}
        </woot-button>
        <woot-button
          v-if="isFolderView"
          class="apply-filter-btn button-filled text-base px-5 py-3"
          color-scheme=""
          :disabled="!activeFolderNewName"
          @click="updateSavedCustomViews"
        >
          {{ $t('FILTER.UPDATE_BUTTON_LABEL') }}
        </woot-button>
        <woot-button
          v-else
          class="apply-filter-btn button-filled text-base px-5 py-3"
          color-scheme=""
          @click="submitFilterQuery"
        >
          {{ $t('FILTER.APPLY_BUTTON_LABEL') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import FilterInputBox from '../FilterInput/IndexV2.vue';
import languages from './advancedFilterItems/languages';
import countries from 'shared/constants/countries.js';
import { mapGetters } from 'vuex';
import { filterAttributeGroups } from './advancedFilterItems';
import filterMixin from 'shared/mixins/filterMixin';
import * as OPERATORS from 'dashboard/components/widgets/FilterInput/FilterOperatorTypes.js';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';

export default {
  components: {
    FilterInputBox,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    initialFilterTypes: {
      type: Array,
      default: () => [],
    },
    initialAppliedFilters: {
      type: Array,
      default: () => [],
    },
    activeFolderName: {
      type: String,
      default: '',
    },
    isFolderView: {
      type: Boolean,
      default: false,
    },
    skipHeader: {
      type: Boolean,
      default: false,
    },
    skipButtons: {
      type: Boolean,
      default: false,
    },
    skipShowFilters: {
      type: Boolean,
      default: false,
    },
  },
  validations: {
    appliedFilters: {
      required,
      $each: {
        values: {
          ensureBetween0to999(value, prop) {
            if (prop.filter_operator === 'days_before') {
              return parseInt(value, 10) > 0 && parseInt(value, 10) < 999;
            }
            return true;
          },
          required: requiredIf(prop => {
            return !(
              prop.filter_operator === 'is_present' ||
              prop.filter_operator === 'is_not_present'
            );
          }),
        },
      },
    },
  },
  data() {
    return {
      show: true,
      appliedFilters: this.initialAppliedFilters,
      activeFolderNewName: this.activeFolderName,
      filterTypes: this.initialFilterTypes,
      filterAttributeGroups,
      filterGroups: [],
      allCustomAttributes: [],
      attributeModel: 'conversation_attribute',
      filtersFori18n: 'FILTER',
      isOverflowing: false,
    };
  },
  computed: {
    ...mapGetters({
      getAppliedConversationFilters: 'getAppliedConversationFilters',
      currentUser: 'getCurrentUser',
    }),
    filterModalHeaderTitle() {
      return !this.isFolderView
        ? this.$t('FILTER.TITLE')
        : this.$t('FILTER.EDIT_CUSTOM_FILTER');
    },
    filterModalSubTitle() {
      return !this.isFolderView
        ? this.$t('FILTER.SUBTITLE')
        : this.$t('FILTER.CUSTOM_VIEWS_SUBTITLE');
    },
  },
  watch: {
    appliedFilters: {
      handler(value) {
        this.$emit('onFiltersChange', value);
      },
    },
  },
  mounted() {
    this.setFilterAttributes();
    this.$store.dispatch('campaigns/get');
    if (this.getAppliedConversationFilters.length && !this.skipShowFilters) {
      this.appliedFilters = [];
      this.appliedFilters = [...this.getAppliedConversationFilters];
    } else if (!this.isFolderView) {
      // d99d commented out wait when they fix this
      // this.appliedFilters.push({
      //   attribute_key: 'status',
      //   filter_operator: 'equal_to',
      //   values: '',
      //   query_operator: 'and',
      //   attribute_model: 'standard',
      // });
    }
  },
  methods: {
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        case 'text':
          return 'plain_text';
        case 'list':
          return 'search_select';
        case 'checkbox':
          return 'search_select';
        default:
          return 'plain_text';
      }
    },
    getAttributeModel(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type.attributeModel;
    },
    getInputType(key, operator) {
      if (key === 'created_at' || key === 'last_activity_at')
        if (operator === 'days_before') return 'plain_text';
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.inputType;
    },
    getOperators(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.filterOperators;
    },
    getDropdownValues(type) {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_ADVANCED_FILTER_ITEMS'); // d99d
      const allCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ](this.attributeModel);

      const isCustomAttributeCheckbox = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type &&
          attr.attribute_display_type === 'checkbox'
        );
      });

      if (isCustomAttributeCheckbox) {
        return [
          {
            id: true,
            name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
          },
          {
            id: false,
            name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
          },
        ];
      }

      const isCustomAttributeList = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type && attr.attribute_display_type === 'list'
        );
      });

      if (isCustomAttributeList) {
        return allCustomAttributes
          .find(attr => attr.attribute_key === type)
          .attribute_values.map(item => {
            return {
              id: item,
              name: item,
            };
          });
      }

      switch (type) {
        case 'status':
          return [
            ...Object.keys(statusFilters).map(status => {
              return {
                id: status,
                name: statusFilters[status].TEXT,
              };
            }),
          ];
        case 'assignee_id':
          return this.$store.getters['agents/getAgents'];
        case 'contact':
          return this.$store.getters['contacts/getContacts'];
        case 'inbox_id':
          return this.$store.getters['inboxes/getInboxes'];
        case 'team_id':
          return this.$store.getters['teams/getTeams'];
        case 'campaign_id':
          return this.$store.getters['campaigns/getAllCampaigns'].map(i => {
            return {
              id: i.database_id,
              name: i.title,
            };
          });
        case 'labels':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.title,
              name: i.title,
            };
          });
        case 'browser_language':
          return languages;
        case 'country_code':
          return countries;
        default:
          return undefined;
      }
    },
    appendNewFilter() {
      if (this.isFolderView) {
        this.setQueryOperatorOnLastQuery();
      } else {
        this.appliedFilters.push({
          attribute_key: 'status',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      }
      this.checkOverflow();
    },
    setQueryOperatorOnLastQuery() {
      const lastItemIndex = this.appliedFilters.length - 1;
      this.appliedFilters[lastItemIndex] = {
        ...this.appliedFilters[lastItemIndex],
        query_operator: 'and',
      };
      this.$nextTick(() => {
        this.appliedFilters.push({
          attribute_key: 'status',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      });
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
        this.checkOverflow();
      }
    },
    checkOverflow() {
      const el = this.$refs.filtersBody;
      if (el) {
        this.isOverflowing =
          el.scrollHeight > 500 && this.appliedFilters.length > 1;
      }
    },
    isFiltersValid() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    submitFilterQuery() {
      if (!this.isFiltersValid) return;
      this.$emit('applyFilter', this.appliedFilters);
      this.$store.dispatch(
        'setConversationFilters',
        JSON.parse(JSON.stringify(this.appliedFilters))
      );
      this.$track(CONVERSATION_EVENTS.APPLY_FILTER, {
        applied_filters: this.appliedFilters.map(filter => ({
          key: filter.attribute_key,
          operator: filter.filter_operator,
          query_operator: filter.query_operator,
        })),
      });
    },
    updateSavedCustomViews() {
      this.$emit('updateFolder', this.appliedFilters, this.activeFolderNewName);
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
  },
};
</script>
<style lang="scss">
.conversation-advanced-filter-modal {
  .modal-container {
    @apply rounded-[12px];
  }
}
</style>
<style lang="scss" scoped>
::v-deep {
  .apply-filter-btn,
  .cancel-filter-btn {
    height: unset;
    .button__content {
      line-height: 22px !important;
    }
  }
}
.folder-input {
  @apply w-[50%] rounded-[8px] border-gray-300 h-[44px] px-3 py-[10px] hover:border-gray-400 focus:border-gray-400;
}
.filters-body {
  @apply flex flex-col justify-between;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
}
</style>
